/**
 * Кадры первого экрана.
 *
 * Раньше туда шли обложки квестов из каталога: они меняются сами собой, и
 * первое, что видит незнакомый человек, зависело от того, чей квест последним
 * прошёл модерацию. Теперь кадры выбирает редакция.
 *
 * Порядок задаётся числом, а не перетаскиванием: кадров единицы, а drag-and-drop
 * в таблице — заметный кусок кода ради того, что делается вводом «1, 2, 3».
 */
import { useState } from "react";
import { DeleteButton, List, useTable } from "@refinedev/antd";
import { useCreate, type HttpError } from "@refinedev/core";
import { Alert, Button, Card, Form, InputNumber, Space, Table, Typography, message } from "antd";

import { ImageUpload } from "../../components/ImageUpload";
import { request } from "../../lib/http";

interface LandingPhoto {
  id: string;
  objectKey?: string;
  imageUrl?: string;
  position?: number;
}

export function LandingPhotoList() {
  const { tableProps, tableQuery } = useTable<LandingPhoto, HttpError>({
    resource: "landing-photos",
    sorters: { mode: "off" },
    pagination: { mode: "off" },
  });

  const [form] = Form.useForm();
  const { mutate: create, mutation: creating } = useCreate();
  const [busy, setBusy] = useState<string | null>(null);

  const add = (values: { objectKey: string; position?: number }) => {
    create(
      { resource: "landing-photos", values: { ...values, position: values.position ?? 0 } },
      {
        onSuccess: () => {
          message.success("Кадр добавлен");
          form.resetFields();
          void tableQuery.refetch();
        },
        onError: (error) => message.error(error.message),
      },
    );
  };

  const move = async (photo: LandingPhoto, position: number) => {
    setBusy(photo.id);
    try {
      await request(`/api/v1/admin/landing-photos/${photo.id}`, {
        method: "PUT",
        body: { objectKey: photo.objectKey, position },
      });
      void tableQuery.refetch();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Не удалось изменить порядок");
    } finally {
      setBusy(null);
    }
  };

  const photos = tableProps.dataSource ?? [];

  return (
    <List title="Первый экран" canCreate={false}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Эти кадры видит незарегистрированный посетитель"
        description="Они медленно сменяются на фоне первого экрана. Показываются первые пять по порядку; если не добавить ни одного, фон останется без снимков."
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={add}>
          <Space align="start" wrap>
            <Form.Item
              name="objectKey"
              label="Изображение"
              rules={[{ required: true, message: "Загрузите изображение" }]}
              style={{ width: 320 }}
            >
              <ImageUpload />
            </Form.Item>

            <Form.Item name="position" label="Порядок" initialValue={photos.length + 1}>
              <InputNumber min={0} max={999} style={{ width: 120 }} />
            </Form.Item>

            <Form.Item label=" ">
              <Button type="primary" htmlType="submit" loading={creating.isPending}>
                Добавить
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </Card>

      <Table {...tableProps} rowKey="id" pagination={false}>
        <Table.Column
          title="Кадр"
          dataIndex="imageUrl"
          render={(url?: string) =>
            url ? (
              <img src={url} alt="" style={{ width: 200, height: 112, objectFit: "cover", borderRadius: 6 }} />
            ) : (
              <Typography.Text type="secondary">нет изображения</Typography.Text>
            )
          }
        />
        <Table.Column
          title="Порядок"
          dataIndex="position"
          width={200}
          render={(position: number, item: LandingPhoto) => (
            <InputNumber
              min={0}
              max={999}
              value={position}
              disabled={busy === item.id}
              onChange={(value) => value !== null && void move(item, value)}
            />
          )}
        />
        <Table.Column
          title=""
          width={80}
          render={(_, item: LandingPhoto) => (
            <DeleteButton hideText size="small" resource="landing-photos" recordItemId={item.id} />
          )}
        />
      </Table>
    </List>
  );
}
